import React, { useState, useEffect } from 'react';
import { 
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { usePrompts } from '../hooks/usePrompts';
import { usePrompt } from '../hooks/usePrompt';
import { Prompt } from '../types';
import PromptList from '../components/prompts/PromptList';
import PromptDetail from '../components/prompts/PromptDetail';
import SortFilter from '../components/filters/SortFilter';
import Pagination from '../components/common/Pagination';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import toast from 'react-hot-toast';

interface HomePageProps {
  searchValue: string;
  selectedCategory: number | null;
  selectedTags: string[];
}

const HomePage: React.FC<HomePageProps> = ({
  searchValue,
  selectedCategory,
  selectedTags,
}) => {
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);

  // 프롬프트 데이터
  const {
    prompts,
    loading,
    error,
    pagination,
    filters,
    searchPrompts,
    filterByCategory,
    filterByTags,
    changePage,
    changeSorting,
    clearFilters,
  } = usePrompts();

  // 선택된 프롬프트 상세 정보
  const { 
    prompt: selectedPrompt,
    loading: promptLoading,
    copyToClipboard,
    logUsage,
  } = usePrompt(selectedPromptId);

  // 검색 및 필터 변경 시 프롬프트 목록 업데이트
  useEffect(() => {
    if (searchValue !== filters.search) {
      searchPrompts(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (selectedCategory !== filters.category) {
      filterByCategory(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (JSON.stringify(selectedTags) !== JSON.stringify(filters.tags)) {
      filterByTags(selectedTags);
    }
  }, [selectedTags]);

  const handleSelectPrompt = (prompt: Prompt) => {
    setSelectedPromptId(prompt.id);
  };

  const handleClosePromptDetail = () => {
    setSelectedPromptId(null);
  };

  const handleCopyPrompt = (content: string) => {
    copyToClipboard(content);
  };

  const handleToggleFavorite = (promptId: number) => {
    setFavoriteIds(prev => 
      prev.includes(promptId)
        ? prev.filter(id => id !== promptId)
        : [...prev, promptId]
    );
    
    const action = favoriteIds.includes(promptId) ? '제거' : '추가';
    toast.success(`즐겨찾기에 ${action}되었습니다.`);
  };

  const handleRatePrompt = (promptId: number, rating: number) => {
    logUsage(rating);
    toast.success('평가해주셔서 감사합니다!');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchValue) count++;
    if (selectedCategory) count++;
    if (selectedTags.length > 0) count++;
    return count;
  };

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            프롬프트 라이브러리
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            173개의 비즈니스 전용 프롬프트로 아이디어를 현실로 만들어보세요
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          {/* 레이아웃 토글 */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded-md transition-colors ${
                layout === 'grid' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setLayout('list')}
              className={`p-2 rounded-md transition-colors ${
                layout === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>

          {/* 정렬 */}
          <SortFilter
            currentSort={filters.sort || 'created_at'}
            currentOrder={filters.order || 'DESC'}
            onSortChange={changeSorting}
          />
        </div>
      </div>

      {/* 활성 필터 표시 */}
      {getActiveFiltersCount() > 0 && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>활성 필터:</span>
              {searchValue && (
                <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded">
                  검색: "{searchValue}"
                </span>
              )}
              {selectedCategory && (
                <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded">
                  카테고리 선택됨
                </span>
              )}
              {selectedTags.length > 0 && (
                <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded">
                  태그: {selectedTags.length}개
                </span>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
            >
              모든 필터 지우기
            </Button>
          </div>
        </Card>
      )}

      {/* 프롬프트 목록 */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {pagination.total}개 중 {pagination.page * pagination.limit - pagination.limit + 1}-
            {Math.min(pagination.page * pagination.limit, pagination.total)}개 표시
          </p>
        </div>

        <PromptList
          prompts={prompts}
          loading={loading}
          error={error}
          onSelectPrompt={handleSelectPrompt}
          onCopyPrompt={handleCopyPrompt}
          onToggleFavorite={handleToggleFavorite}
          favoriteIds={favoriteIds}
          layout={layout}
          emptyMessage={
            getActiveFiltersCount() > 0 
              ? '검색 조건에 맞는 프롬프트가 없습니다.' 
              : '프롬프트를 불러오는 중입니다...'
          }
        />

        {/* 페이지네이션 */}
        {pagination.pages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={changePage}
          />
        )}
      </div>

      {/* 프롬프트 상세 모달 */}
      <PromptDetail
        prompt={selectedPrompt}
        isOpen={!!selectedPromptId}
        onClose={handleClosePromptDetail}
        onCopy={handleCopyPrompt}
        onToggleFavorite={handleToggleFavorite}
        onRate={handleRatePrompt}
        isFavorite={selectedPromptId ? favoriteIds.includes(selectedPromptId) : false}
      />
    </div>
  );
};

export default HomePage;